import * as React from 'react'
import { Button, Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap'
import { LoaderPlaceholder } from '@components/loader-placeholder'
import { BaseModalProps } from '@components/modals/types'
import { ProductPackage, ProductPackageDetails } from '@modules/product-package/models'
import { useGetEndlessHolidayTransactionDetailsQuery } from '@api/endless-holiday'
import { TypedQueryResult } from '@api/base'
import { EndlessHolidayDetailsTabs } from '@modules/product-package/transactions/details/tabs'
import { EndlessHolidayDetailsBaseData } from '@modules/product-package/transactions/details/base-data'
import { createInvoiceData, ProductInvoiceCard } from '@components/products/product-invoice-card'
import { createProductCardClient, ProductClientCard } from '@components/products/product-client-card'
import { ContentNotesCard } from '@components/content-notes/content-notes-card'
import { HistoryBox } from '@modules/promotions/common/history-box'
import { PackageWholesaleDetailsSubscriptions } from '@modules/package-wholesale/details/subscriptions'
import { PackageWholesaleDetailsGastroCards } from '@modules/package-wholesale/details/gastro_cards'
import { NotificationBox } from '@components/notification-box/notification-box'
import { useAppData } from '@components/hooks/use-app-data'
import { EndlessHolidayDetailsModalDeleteButton } from '@modules/product-package/transactions/details/delete'
import { useDetailsModalUnmountParamsClear } from '@components/hooks/use-details-modal'
import { useNotificationTemplates } from '@components/notification-box/use-notification-templates'
import { useProductsAppData } from '@modules/products/hooks/use-products-app-data'
import { getById } from '@helpers/utils'

interface Props extends BaseModalProps {
  product: ProductPackage
}

export const EndlessHolidayDetailsModal: React.FC<Props> = ({ product, toggleIsVisible }) => {
  const { content_types } = useAppData()
  const { product_package_sets } = useProductsAppData()

  const historyRef = React.useRef<HTMLDivElement>(null)
  const informationRef = React.useRef<HTMLDivElement>(null)
  const gastroCardsRef = React.useRef<HTMLDivElement>(null)
  const subscriptionsRef = React.useRef<HTMLDivElement>(null)

  useDetailsModalUnmountParamsClear(product.id)

  const {
    data: productDetails,
    isLoading,
    refetch,
  } = useGetEndlessHolidayTransactionDetailsQuery(product.urls.details) as TypedQueryResult<ProductPackageDetails>

  const { notificationTemplates } = useNotificationTemplates({ url: productDetails?.urls.notifications })

  const title = {
    endless_holiday: `Pakiet "Wakacje w nieskończoność" ${product.number}`,
    voucher: getById(product_package_sets, productDetails?.product_package_set.id ?? product.product_package_set)
      ?.is_winter
      ? 'Pakiet Zimowy'
      : 'Voucher świąteczny',
  }[product.kind]

  return (
    <>
      <ModalHeader toggle={toggleIsVisible}>{title}</ModalHeader>
      <ModalBody className="modal-details">
        <LoaderPlaceholder content={!isLoading}>
          {productDetails && (
            <>
              <EndlessHolidayDetailsTabs
                subscriptionsRef={subscriptionsRef}
                gastroCardsRef={gastroCardsRef}
                informationRef={informationRef}
                historyRef={historyRef}
              />
              <Row>
                <EndlessHolidayDetailsBaseData productDetails={productDetails} />
                <Col md={6}>
                  <ProductClientCard clientData={createProductCardClient(productDetails.client)} canViewClientDetails />
                  <ProductInvoiceCard invoiceData={createInvoiceData(productDetails)} />
                </Col>
                <Col md={12}>
                  <div ref={subscriptionsRef}>
                    <PackageWholesaleDetailsSubscriptions
                      subscriptions={productDetails.subscriptions}
                      defaultStatusesToShow={['initial', 'confirmed', 'unfinished']}
                    />
                  </div>
                  <div ref={gastroCardsRef}>
                    <PackageWholesaleDetailsGastroCards gastroCards={productDetails.gastro_cards} />
                  </div>
                </Col>
                <Col md={6}>
                  <NotificationBox
                    emailTemplates={notificationTemplates.email}
                    emailNotifications={productDetails.notifications}
                    emailUrl={productDetails.urls.notifications}
                    onNotificationSent={refetch}
                  />
                </Col>
                <Col md={6}>
                  <ContentNotesCard
                    objectId={productDetails.id}
                    contentType={content_types.productpackage}
                    notes={productDetails.notes}
                    onNotesUpdate={refetch}
                    readOnly={false}
                  />
                  <HistoryBox history={productDetails.history} ref={historyRef} />
                </Col>
              </Row>
            </>
          )}
        </LoaderPlaceholder>
      </ModalBody>
      <ModalFooter>
        {productDetails && productDetails.status !== 'removed' && (
          <EndlessHolidayDetailsModalDeleteButton productDetails={productDetails} />
        )}
        <Button className="btn btn-light" type="button" onClick={toggleIsVisible}>
          Zamknij
        </Button>
      </ModalFooter>
    </>
  )
}
